<template>
  <EnWordCardLayout :hide-role-image="props.face.style.hideRoleImage">
    <template #content>
      <DebutCardBadge
        v-if="face.isFirstLearn"
        class="absolute top-0 left-0"
      />
      <WrongCardBadge
        v-if="face.isWrongCard"
        class="absolute top-0 left-0"
      />

      <div class="text-21px leading-normal font-normal tracking-0.5px">
        <WordSearchSpan
          v-for="(span, i) of questionSpans"
          :key="i"
          :class="{
            'text-primary font-semibold': span.highlight,
          }"
          :text="span.text"
        ></WordSearchSpan>
      </div>
    </template>
    <template #options>
      <div class="flex flex-col gap-3">
        <Button
          v-for="(item, i) of options"
          :key="item.word"
          class="option"
          :class="{ wrong: optionsWrongMap[i] }"
          :scene="getOptionScene(item, i)"
          @click="onOptionClick(item, i)"
        >
          <SparkleText
            class="flex flex-col justify-center items-start w-full text-start"
            :tag="item.isAnswer && data.checked"
          >
            <template v-if="optionsWrongMap[i]">
              <div class="text-lg font-semibold leading-26px">
                {{ item.word }}
              </div>
              <div
                class="text-[var(--text-color-secondary)] text-sm leading-22px"
              >
                {{ item.definition }}
              </div>
            </template>
            <div v-else>
              {{ item.definition }}
            </div>
          </SparkleText>
        </Button>
      </div>
    </template>
  </EnWordCardLayout>
</template>
<script setup lang="ts">
import { type ExampleChoiceEnWordFace, UnitEventType } from '@/types/core'
import { getHighlightSpans, randomPick, wait } from '@/utils/misc'
import { reactive, computed, onMounted, inject } from 'vue'
import { genOptions, type Option } from '../common'
import { useHotKey } from '@/hooks'
import { FeedbackStar } from '../../common/feedback'
import SparkleText from '@/components/SparkleText.vue'
import EnWordCardLayout from '../../layout/EnWordCardLayout.vue'
import DebutCardBadge from '@/components/ConcreteCard/common/DebutCardBadge.vue'
import { showCardDetail } from '../../common'
import WordSearchSpan from '@/components/WordSearchSpan/WordSearchSpan.vue'
import WrongCardBadge from '../../common/WrongCardBadge.vue'

useHotKey('1,2,3,4,5,6,7,8,9,0', (evt: KeyboardEvent) => {
  const key = evt.key
  const index = (key === '0' ? 10 : Number(key)) - 1

  if (index >= options.value.length) return

  const op = options.value[index]
  onOptionClick(op, index)
})

const onNext = inject<VoidFunction>('onNext')
const onEvent = inject<(event: UnitEventType) => void>('onEvent')
const onStar = inject<(star: FeedbackStar) => void>('onStar')

const props = defineProps<{
  face: ExampleChoiceEnWordFace
}>()

const data = reactive({
  checked: false,
  tipTimes: 0,
  star: FeedbackStar.One,
  selectedWrongOptions: new Set<number>(),
})

const options = computed(() => genOptions(props.face.card, props.face.altCards))

const optionsWrongMap = computed(() => {
  const map: Record<number, boolean> = {}

  for (let i = 0; i < options.value.length; i++) {
    map[i] = data.selectedWrongOptions.has(i)
  }

  return map
})

const questionExample = computed(() => {
  return randomPick(props.face.card.examples)
})

const questionSpans = computed(() => {
  if (!questionExample.value) return []

  return getHighlightSpans(questionExample.value.source, props.face.card.word)
})

onMounted(async () => {
  _am.playPron(questionExample.value.source)
})

function getOptionScene(
  op: Option,
  index: number
):
  | 'choice'
  | 'choiceSelected'
  | 'choiceCorrect'
  | 'choiceWrong'
  | 'choiceUsed' {
  let wrong = optionsWrongMap.value[index]
  let correct = op.isAnswer && data.checked

  if (correct) {
    return 'choiceCorrect'
  }
  if (wrong) {
    return 'choiceWrong'
  }

  return 'choice'
}

// beforeNext 表示自动下一张之前需要等待完成的一些内容
// 目前这里等待的都是发音播放
async function onAutoNext(beforeNext: Promise<void>) {
  // 先等待按钮动画结束
  await wait(700)

  // 如果是新知识且设置的展示卡片详情，此时会先弹出卡片详情
  // 且无需等待发音播放完成
  if (props.face.isFirstLearn && props.face.style.showCardDetail) {
    showCardDetail(props.face.card, props.face.isFirstLearn, onNext)
    return
  }

  try {
    await beforeNext
  } finally {
    onNext?.()
  }
}

async function onOptionClick(option: Option, index: number) {
  if (data.checked) return

  if (option.isAnswer) {
    data.checked = true
    onEvent?.(UnitEventType.CORRECT)
  } else {
    data.tipTimes++
    data.selectedWrongOptions.add(index)
    onEvent?.(UnitEventType.WRONG)
  }

  data.star = data.tipTimes > 0 ? FeedbackStar.Two : FeedbackStar.Three

  if (data.checked) {
    onStar?.(data.star)
  }

  if (data.checked) {
    // TODO(buding): 这里不光是要等待音频播放完成，还需要等待按钮动画（同 Cloze 卡面）
    // 其他英语卡面都需要处理
    onAutoNext(_am.playPron(option.word))
  } else {
    _am.playPron(option.word)
  }
}
</script>

<style scoped>
.option {
  min-height: 66px;
  position: relative;
}
.option.wrong {
  animation: shake 0.2s linear 0.2s;
}
</style>
